import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import {
  Menu,
  X,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  Shield,
  Heart,
  Sparkles,
  Smile,
  User,
  Mail,
  MessageSquare,
  ArrowRight,
  ChevronDown,
  Building2 } from
"lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "5491141910715";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Hola%20Dr.%20Cuello,%20quisiera%20solicitar%20un%20turno.`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_drcuello-odonto/artifacts/6egxgycc_logo.png";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#inicio", label: "Inicio" },
  { href: "#sobre-mi", label: "Sobre Mí" },
  { href: "#experiencia", label: "Experiencia" },
  { href: "#servicios", label: "Servicios" },
  { href: "#confianza", label: "Confianza" },
  { href: "#consultorios", label: "Consultorios" },
  { href: "#contacto", label: "Contacto" }];


  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? "glass-nav shadow-sm" : "bg-transparent"}`
      }>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#inicio" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Dr. Alejandro Cuello" className="h-12 w-12 object-contain" />
            <span className="font-display text-xl md:text-2xl font-semibold text-slate-900">
              Dr. Alejandro Cuello
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className="nav-link font-body text-sm text-slate-600 hover:text-slate-900 transition-colors"
              data-testid={`nav-link-${link.href.replace('#', '')}`}>

                {link.label}
              </a>
            )}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="nav-cta-button"
                className="btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">

                Reservar Turno
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>

            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-0 top-20 bg-white z-40 mobile-menu ${
        isMobileMenuOpen ? "open" : ""}`
        }>

        <div className="flex flex-col p-6 space-y-6">
          {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            className="font-body text-lg text-slate-700 hover:text-slate-900"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid={`mobile-nav-${link.href.replace('#', '')}`}>

              {link.label}
            </a>
          )}
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="mobile-cta-button"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full">

              Reservar Turno
            </Button>
          </a>
        </div>
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">

      {/* Background texture */}
      <div className="absolute inset-0 texture-overlay hero-gradient"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <span className="inline-block font-body uppercase animate-fade-in !text-sm mb-6 text-slate-500">Odontología de calidad

            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold text-slate-900 mb-6 animate-fade-in-up">
              Sonreír con confianza empieza con una atención profesional
            </h1>
            <p className="font-body md:text-xl max-w-xl lg:mx-0 animate-fade-in-up delay-200 !text-lg mb-10 mx-auto text-slate-600">Odontología moderna, precisa y humana. Cuido cada detalle de tu salud bucal con atención personalizada.

            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up delay-300">
              <a href="#contacto">
                <Button
                  data-testid="hero-cta-primary"
                  className="btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-base">

                  Reservar Turno
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="hero-cta-whatsapp"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base border-slate-300 hover:bg-slate-50">

                  <Phone className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>

          {/* Image Placeholder */}
          <div className="animate-fade-in-up delay-400">
            <div
              data-testid="hero-image-placeholder"
              className="image-placeholder aspect-[4/5] rounded-2xl overflow-hidden">

              <div className="flex flex-col items-center justify-center h-full p-8">
                <User className="h-16 w-16 text-slate-300 mb-4" />
                <span className="text-slate-400 font-body text-sm text-center">
                  Foto del Dr. Alejandro Cuello
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-float hidden md:block">
          <a href="#sobre-mi" className="flex flex-col items-center text-slate-400 hover:text-slate-600 transition-colors">
            <span className="text-xs font-body mb-2">Descubrir más</span>
            <ChevronDown className="h-5 w-5" />
          </a>
        </div>
      </div>
    </section>);

};

// About Section
const AboutSection = () => {
  return (
    <section
      id="sobre-mi"
      data-testid="about-section"
      className="py-24 md:py-32 bg-white">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Placeholder */}
          <div className="order-2 lg:order-1">
            <div
              data-testid="about-image-placeholder"
              className="image-placeholder aspect-square rounded-2xl overflow-hidden">

              <div className="flex flex-col items-center justify-center h-full p-8">
                <User className="h-20 w-20 text-slate-300 mb-4" />
                <span className="text-slate-400 font-body text-sm text-center">
                  Foto profesional del Doctor
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block font-body text-sm tracking-widest text-slate-500 uppercase mb-4">
              Sobre el Doctor
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
              Dr. Alejandro Cuello
            </h2>
            <div className="space-y-4 text-slate-600 font-body text-base md:text-lg">
              <p>
                Con años de dedicación a la odontología, he construido una reputación basada en la excelencia profesional y el trato humano.
              </p>
              <p>
                Mi filosofía de trabajo se centra en escuchar a cada paciente, entender sus necesidades y brindar soluciones personalizadas con los más altos estándares de calidad.
              </p>
              <p>
                Cada consulta es una oportunidad para generar confianza y cuidar tu salud bucal con precisión, tecnología moderna y calidez humana.
              </p>
            </div>

            {/* Quote */}
            <div className="mt-10 relative pl-6 border-l-2 border-slate-200">
              <p className="font-display md:text-2xl !text-xl !italic text-slate-800">"Mi compromiso es que cada paciente se sienta escuchado, cuidado y seguro en cada visita."
              </p>
              <span className="block mt-4 font-body text-sm text-slate-500">
                — Dr. Alejandro Cuello
              </span>
            </div>

            <div className="mt-10">
              <a href="#contacto">
                <Button
                  data-testid="about-cta-button"
                  className="btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8">

                  Conocer más
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Experience Section
const ExperienceSection = () => {
  const experiences = [
  {
    year: "1996",
    title: "T\xE9cnico Protesista Dental",
    institution: "Instituto Dr. Pierre Fouchard",
    description: "Formación técnica especializada"
  },
  {
    year: "2003",
    title: "Odontólogo",
    institution: "Universidad de Buenos Aires (UBA)",
    description: "Facultad de Odontología"
  }];


  const specializations = [
  "Cirugía Oral y Maxilofacial",
  "Implantología",
  "Endodoncia",
  "Rehabilitación Oral",
  "Farmacología Odontológica"];


  const highlights = [
  {
    number: "+20",
    label: "Años de experiencia"
  },
  {
    number: "2003",
    label: "Graduado UBA"
  },
  {
    number: "2",
    label: "Consultorios"
  }];


  return (
    <section
      id="experiencia"
      data-testid="experience-section"
      className="py-24 md:py-32 bg-slate-50">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block font-body text-sm tracking-widest text-slate-500 uppercase mb-4">
            Trayectoria
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
            Experiencia Profesional
          </h2>
          <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
            Más de dos décadas dedicado a la odontología, con formación continua y especializaciones en las áreas más demandadas.
          </p>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-16">
          {highlights.map((item, index) =>
          <div key={index} className="text-center">
              <span className="block font-display text-4xl md:text-5xl font-semibold text-slate-900">
                {item.number}
              </span>
              <span className="block font-body text-sm text-slate-600 mt-2">
                {item.label}
              </span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Education */}
          <div>
            <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">
              Formación Académica
            </h3>
            <div className="space-y-6">
              {experiences.map((exp, index) =>
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                  <span className="inline-block font-body text-sm text-slate-500 mb-2">
                    {exp.year}
                  </span>
                  <h4 className="font-display text-xl font-semibold text-slate-900 mb-1">
                    {exp.title}
                  </h4>
                  <p className="font-body text-slate-700">{exp.institution}</p>
                  <p className="font-body text-sm text-slate-500 mt-1">{exp.description}</p>
                </div>
              )}
            </div>

            {/* Work experience highlight */}
            <div className="mt-6 bg-white rounded-xl p-6 border border-slate-200">
              <span className="inline-block font-body text-sm text-slate-500 mb-2">
                2004 - 2006
              </span>
              <h4 className="font-display text-xl font-semibold text-slate-900 mb-1">
                Subjefe de Guardia
              </h4>
              <p className="font-body text-slate-700">Facultad de Odontología - UBA</p>
              <p className="font-body text-sm text-slate-500 mt-1">Servicio de urgencias odontológicas</p>
            </div>
          </div>

          {/* Specializations */}
          <div>
            <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">
              Especializaciones
            </h3>
            <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
              <p className="font-body text-slate-600 mb-4">
                Formación continua en las áreas más avanzadas de la odontología moderna:
              </p>
              <div className="flex flex-wrap gap-2">
                {specializations.map((spec, index) =>
                <span
                  key={index}
                  className="inline-block bg-slate-100 text-slate-700 font-body text-sm px-4 py-2 rounded-full">

                    {spec}
                  </span>
                )}
              </div>
            </div>

            {/* Private practice */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
              <h4 className="font-display text-xl font-semibold text-slate-900 mb-4">
                Práctica Privada
              </h4>
              <div className="space-y-3 font-body text-slate-600">
                <div className="flex justify-between">
                  <span>Lanús</span>
                  <span className="text-slate-500">desde 2004</span>
                </div>
                <div className="flex justify-between">
                  <span>Pilar</span>
                  <span className="text-slate-500">desde 2018</span>
                </div>
              </div>
            </div>

            {/* Download CV button */}
            <a
              href="https://customer-assets.emergentagent.com/job_drcuello-odonto/artifacts/4bx0v5qc_DOC-20220914-WA0047..pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="block">

              <Button
                data-testid="download-cv-button"
                variant="outline"
                className="w-full rounded-full border-slate-300 hover:bg-slate-100">

                <ArrowRight className="mr-2 h-4 w-4" />
                Descargar CV completo
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    icon: <Smile className="h-8 w-8" />,
    title: "Odontología General",
    description: "Diagnóstico completo, tratamientos preventivos y soluciones integrales para tu salud bucal.",
    large: true
  },
  {
    icon: <Sparkles className="h-8 w-8" />,
    title: "Limpieza Profesional",
    description: "Higiene profunda para mantener tus dientes sanos y brillantes.",
    large: false
  },
  {
    icon: <Heart className="h-8 w-8" />,
    title: "Estética Dental",
    description: "Mejorá la apariencia de tu sonrisa con tratamientos estéticos personalizados.",
    large: false
  },
  {
    icon: <Sparkles className="h-8 w-8" />,
    title: "Blanqueamiento",
    description: "Dientes más blancos y luminosos con técnicas seguras y efectivas.",
    large: false
  },
  {
    icon: <Shield className="h-8 w-8" />,
    title: "Cirugía Dentomaxilar",
    description: "Procedimientos quirúrgicos especializados para resolver problemas complejos de salud bucal.",
    large: false
  },
  {
    icon: <CheckCircle2 className="h-8 w-8" />,
    title: "Implantología",
    description: "Implantes dentales de alta calidad para recuperar tu sonrisa de forma permanente.",
    large: false
  },
  {
    icon: <User className="h-8 w-8" />,
    title: "Atención Personalizada",
    description: "Cada paciente es único. Diseño planes de tratamiento adaptados a tus necesidades específicas, con presupuestos según tu caso particular.",
    large: true
  }];


  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="py-24 md:py-32 bg-slate-50">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block font-body uppercase !text-sm mb-4 text-slate-500">

          </span>
          <h2 className="font-display md:text-5xl !font-semibold !text-4xl mb-6 text-slate-900">Servicios

          </h2>
          <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
            Ofrezco una amplia gama de servicios odontológicos con los más altos estándares de calidad. Los precios se basan en el caso concreto de cada paciente.
          </p>
        </div>

        <div className="bento-grid">
          {services.map((service, index) =>
          <div
            key={index}
            data-testid={`service-card-${index}`}
            className={`service-card bg-white rounded-xl p-8 ${
            service.large ? "bento-large" : ""}`
            }>

              <div className="text-slate-700 mb-6">{service.icon}</div>
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-3">
                {service.title}
              </h3>
              <p className="font-body text-slate-600">{service.description}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Trust Section
const TrustSection = () => {
  const trustPoints = [
  {
    icon: <Shield className="h-10 w-10" />,
    title: "Atención Personalizada",
    description: "Cada paciente recibe un trato único, con tiempo y dedicación para escuchar y resolver todas sus inquietudes."
  },
  {
    icon: <Clock className="h-10 w-10" />,
    title: "Puntualidad Requerida",
    description: "Se asigna la duraci\xF3n del turno seg\xFAn la necesidad del tratamiento, evitando as\xED, largas esperas. Es fundamental que los pacientes cumplan con el horario asignado, sin excepci\xF3n."
  },
  {
    icon: <Sparkles className="h-10 w-10" />,
    title: "Turnos Rápidos",
    description: "Asignaci\xF3n din\xE1mica de turnos. Se agendan turnos dentro de las dos semanas de plazo."
  },
  {
    icon: <Heart className="h-10 w-10" />,
    title: "Trato Cercano y Profesional",
    description: "Un ambiente c\xE1lido donde te sentir\xE1s c\xF3modo y seguro. Trabajo solo, lo que garantiza atenci\xF3n directa, tranquila y personalizada."
  }];


  return (
    <section
      id="confianza"
      data-testid="trust-section"
      className="py-24 md:py-32 bg-white">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block font-body text-sm tracking-widest text-slate-500 uppercase mb-4">
            Tu Tranquilidad
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
            Tu salud bucal en manos profesionales
          </h2>
          <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
            Atención clara, sin apuros. Me tomo el tiempo necesario para brindarte el mejor servicio.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {trustPoints.map((point, index) =>
          <div
            key={index}
            data-testid={`trust-point-${index}`}
            className="flex gap-6 p-6 rounded-xl hover:bg-slate-50 transition-colors">

              <div className="flex-shrink-0 text-slate-700">{point.icon}</div>
              <div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                  {point.title}
                </h3>
                <p className="font-body text-slate-600">{point.description}</p>
              </div>
            </div>
          )}
        </div>

        {/* Testimonial quote */}
        <div className="mt-20 text-center max-w-3xl mx-auto">
          <div className="font-display text-6xl text-slate-200 mb-4">"</div>
          <p className="font-display text-2xl md:text-3xl text-slate-800 italic mb-6">
            Tu sonrisa merece la mejor atención. Trabajo para que cada visita sea una experiencia positiva.
          </p>
          <div className="w-16 h-1 bg-slate-200 mx-auto"></div>
        </div>
      </div>
    </section>);

};

// Locations Section
const LocationsSection = () => {
  const locations = [
  {
    name: "Concord Pilar",
    address: "Ruta Panamericana Ramal Pilar Km 49.5, Valentín Gómez 811, La Lonja, Buenos Aires",
    hours: "Turnos con reserva previa",
    detail: "Sector Almendros, Piso 2, Dpto. 206",
    image: "https://customer-assets.emergentagent.com/job_drcuello-odonto/artifacts/rllt8mun_imagen_2026-02-05_155928846.png",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Concord+Pilar+Ruta+Panamericana+ramal+pilar+km+49.5+Valentin+Gomez+811+La+Lonja+Buenos+Aires"
  },
  {
    name: "Consultorio Lanús",
    address: "14 de Julio 3289, Lanús Oeste, Buenos Aires",
    hours: "Turnos con reserva previa",
    detail: null,
    image: "https://customer-assets.emergentagent.com/job_drcuello-odonto/artifacts/6p7hmejg_imagen_2026-02-14_021115705.png",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=14+de+Julio+3289+Lanus+Oeste+Buenos+Aires"
  }];


  return (
    <section
      id="consultorios"
      data-testid="locations-section"
      className="py-24 md:py-32 bg-slate-50">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="inline-block font-body text-sm tracking-widest text-slate-500 uppercase mb-4">
            Ubicaciones
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
            Mis Consultorios
          </h2>
          <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
            Dos ubicaciones para tu comodidad. Todos los turnos son con reserva previa.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {locations.map((location, index) =>
          <div
            key={index}
            data-testid={`location-card-${index}`}
            className="location-card bg-white rounded-xl overflow-hidden border border-slate-200">

              {/* Imagen del consultorio */}
              <div className="aspect-[4/3] w-full">
                {location.image ?
              <img
                src={location.image}
                alt={location.name}
                className="w-full h-full object-cover" /> :


              <div className="image-placeholder w-full h-full flex flex-col items-center justify-center">
                    <MapPin className="h-10 w-10 text-slate-300 mb-2" />
                    <span className="text-slate-400 font-body text-sm text-center px-4">
                      Foto {location.name}
                    </span>
                  </div>
              }
              </div>
              
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-4">
                  {location.name}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-600">{location.address}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-600">{location.hours}</span>
                  </div>
                  {location.detail &&
                <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span className="font-body text-slate-600">{location.detail}</span>
                    </div>
                }
                </div>
                
                <div className="flex flex-col gap-3 mt-6">
                  {location.mapsUrl &&
                <a href={location.mapsUrl} target="_blank" rel="noopener noreferrer">
                      <Button
                    data-testid={`location-maps-${index}`}
                    variant="outline"
                    className="w-full rounded-full border-slate-300 hover:bg-slate-50">

                        <MapPin className="mr-2 h-4 w-4" />
                        Ver en Google Maps
                      </Button>
                    </a>
                }
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    <Button
                    data-testid={`location-cta-${index}`}
                    className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white">

                      <Phone className="mr-2 h-4 w-4" />
                      Reservar turno
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_location: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Construir mensaje de WhatsApp con los datos del formulario
    const locationText = formData.preferred_location ? `Consultorio: ${formData.preferred_location}` : '';
    const serviceText = formData.service ? `Servicio: ${formData.service}` : '';
    const messageText = formData.message ? `Mensaje: ${formData.message}` : '';

    const whatsappMessage = `Hola Dr. Cuello, quisiera solicitar un turno.

*Datos del paciente:*
Nombre: ${formData.name}
Email: ${formData.email}
Teléfono: ${formData.phone}
${locationText}
${serviceText}
${messageText}`.trim();

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    // Abrir WhatsApp en nueva pestaña
    window.open(whatsappUrl, '_blank');

    setIsSubmitted(true);
    toast.success("¡Redirigiendo a WhatsApp!", {
      description: "Completá el envío en WhatsApp para confirmar tu turno."
    });

    setFormData({
      name: "",
      email: "",
      phone: "",
      preferred_location: "",
      service: "",
      message: ""
    });
    setIsSubmitting(false);
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white">

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <span className="inline-block font-body text-sm tracking-widest text-slate-500 uppercase mb-4">
              Contacto
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
              Reservá tu turno
            </h2>
            <p className="font-body text-lg text-slate-600 mb-10">
              Completá el formulario o contactanos directamente por WhatsApp. Responderemos a la brevedad para coordinar tu consulta.
            </p>

            <div className="space-y-6">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                data-testid="contact-whatsapp-link">

                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <span className="block font-body font-medium text-slate-900">WhatsApp</span>
                  <span className="block font-body text-sm text-slate-600">+54 9 11 4191-0715</span>
                </div>
              </a>
            </div>

            {/* Trust badge */}
            <div className="!opacity-[0%]">
              <div className="flex items-center gap-4">
                <Shield className="h-8 w-8 text-slate-700" />
                <div>
                  <span className="block font-body font-medium text-slate-900"></span>
                  <span className="block font-body text-sm text-slate-600">

                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-2xl p-8 md:p-10">
            {isSubmitted ?
            <div className="text-center py-12" data-testid="form-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-slate-900 mb-3">
                  ¡Listo!
                </h3>
                <p className="font-body text-slate-600 mb-6">
                  Se abrió WhatsApp con tus datos. Enviá el mensaje para confirmar tu turno.
                </p>
                <Button
                data-testid="form-reset-button"
                variant="outline"
                onClick={() => setIsSubmitted(false)}
                className="rounded-full">

                  Enviar otra consulta
                </Button>
              </div> :

            <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">
                  Solicitar turno
                </h3>

                <div className="space-y-5">
                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Nombre completo *
                    </label>
                    <Input
                    data-testid="form-input-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Tu nombre"
                    className="rounded-lg border-slate-200 focus:border-slate-400" />

                  </div>

                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Email *
                    </label>
                    <Input
                    data-testid="form-input-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@email.com"
                    className="rounded-lg border-slate-200 focus:border-slate-400" />

                  </div>

                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Teléfono *
                    </label>
                    <Input
                    data-testid="form-input-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+54 9 11 1234-5678"
                    className="rounded-lg border-slate-200 focus:border-slate-400" />

                  </div>

                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Consultorio preferido
                    </label>
                    <Select
                    onValueChange={(value) => handleSelectChange("preferred_location", value)}
                    value={formData.preferred_location}>

                      <SelectTrigger data-testid="form-select-location" className="rounded-lg border-slate-200">
                        <SelectValue placeholder="Seleccionar consultorio" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="concord-pilar">Concord Pilar</SelectItem>
                        <SelectItem value="consultorio-lanus">Consultorio Lanús</SelectItem>
                        <SelectItem value="sin-preferencia">Sin preferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Servicio de interés
                    </label>
                    <Select
                    onValueChange={(value) => handleSelectChange("service", value)}
                    value={formData.service}>

                      <SelectTrigger data-testid="form-select-service" className="rounded-lg border-slate-200">
                        <SelectValue placeholder="Seleccionar servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="odontologia-general">Odontología General</SelectItem>
                        <SelectItem value="limpieza">Limpieza Profesional</SelectItem>
                        <SelectItem value="estetica">Estética Dental</SelectItem>
                        <SelectItem value="blanqueamiento">Blanqueamiento</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block font-body text-sm text-slate-700 mb-2">
                      Mensaje (opcional)
                    </label>
                    <Textarea
                    data-testid="form-input-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Contanos sobre tu consulta..."
                    rows={4}
                    className="rounded-lg border-slate-200 focus:border-slate-400 resize-none" />

                  </div>

                  <Button
                  data-testid="form-submit-button"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6">

                    {isSubmitting ? "Enviando..." : "Enviar solicitud"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

};

// Footer Section
const Footer = () => {
  return (
    <footer data-testid="footer" className="footer-gradient py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-center md:text-left flex items-center gap-4">
            <img src={LOGO_URL} alt="Dr. Alejandro Cuello" className="h-14 w-14 object-contain" />
            <div>
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-1">
                Dr. Alejandro Cuello
              </h3>
              <p className="font-body text-slate-600">
                Odontología moderna, precisa y humana.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-body text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="footer-whatsapp">

              <Phone className="h-4 w-4" />
              +54 9 11 4191-0715
            </a>
            <a href="#contacto">
              <Button
                data-testid="footer-cta-button"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">

                Reservar Turno
              </Button>
            </a>
          </div>
        </div>

        <div className="section-divider my-10"></div>

        <div className="text-center">
          <p className="font-body text-sm text-slate-500">
            © {new Date().getFullYear()} Dr. Alejandro Cuello. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>);

};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-floating-button"
      className="whatsapp-float animate-pulse-subtle"
      aria-label="Contactar por WhatsApp">

      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        className="h-7 w-7">

        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>);

};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="smooth-scroll">
      <Toaster position="top-center" richColors />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ExperienceSection />
      <ServicesSection />
      <TrustSection />
      <LocationsSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>);

};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>);

}

export default App;